// src/utils/sessionMetrics.ts
// Session metrics tracking utility for user analytics
class SessionMetricsTracker {
    constructor() {
        this.activeStartTime = null;
        this.webcamStartTime = null;
        this.totalTimeMs = 0;
        this.activeTimeMs = 0;
        this.inactiveTimeMs = 0;
        this.webcamOnTimeMs = 0;
        this.messagesCount = 0;
        this.isActive = !document.hidden;
        this.isWebcamOn = false;
        this.startTime = Date.now();
        this.lastUpdateTime = this.startTime;
        // Start tracking active time if page is visible
        if (!document.hidden) {
            this.activeStartTime = this.startTime;
        }
        // Listen for visibility changes
        document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }
    /**
     * Handle page visibility changes
     */
    handleVisibilityChange() {
        const now = Date.now();
        if (document.hidden) {
            // Page became hidden
            if (this.activeStartTime !== null) {
                this.activeTimeMs += now - this.activeStartTime;
                this.activeStartTime = null;
            }
            this.isActive = false;
        }
        else {
            // Page became visible
            this.activeStartTime = now;
            this.isActive = true;
        }
    }
    /**
     * Set webcam status
     */
    setWebcamStatus(isOn) {
        const now = Date.now();
        if (isOn && !this.isWebcamOn) {
            // Webcam turned on
            this.webcamStartTime = now;
            this.isWebcamOn = true;
        }
        else if (!isOn && this.isWebcamOn) {
            // Webcam turned off
            if (this.webcamStartTime !== null) {
                this.webcamOnTimeMs += now - this.webcamStartTime;
                this.webcamStartTime = null;
            }
            this.isWebcamOn = false;
        }
    }
    /**
     * Increment message count
     */
    incrementMessageCount() {
        this.messagesCount++;
    }
    /**
     * Get network information
     */
    getNetworkInfo() {
        const nav = navigator;
        const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
        if (!connection) {
            console.log('âš ï¸ Network Information API not available');
            return {
                networkType: 'N/A',
                effectiveType: 'unknown',
                downlink: null,
                rtt: null,
                saveData: false
            };
        }
        const networkType = connection.type || connection.effectiveType || 'unknown';
        console.log('ðŸ“¡ Network info:', {
            type: connection.type,
            effectiveType: connection.effectiveType,
            downlink: connection.downlink,
            rtt: connection.rtt
        });
        return {
            networkType: networkType, // wifi, ethernet, cellular, bluetooth, or fall back to effectiveType (4g, 3g, etc.)
            effectiveType: connection.effectiveType || 'unknown', // slow-2g, 2g, 3g, 4g
            downlink: connection.downlink || null,
            rtt: connection.rtt || null,
            saveData: connection.saveData || false
        };
    }
    /**
     * Get battery information
     */
    async getBatteryInfo() {
        try {
            const nav = navigator;
            if (!nav.getBattery) {
                return { batteryLevel: null, isCharging: null };
            }
            const battery = await nav.getBattery();
            return {
                batteryLevel: Math.round(battery.level * 100),
                isCharging: battery.charging
            };
        }
        catch (e) {
            return { batteryLevel: null, isCharging: null };
        }
    }
    /**
     * Get current metrics snapshot
     */
    async getMetrics() {
        const now = Date.now();
        // Update total time
        this.totalTimeMs = now - this.startTime;
        // Update active time if currently active
        if (this.activeStartTime !== null) {
            this.activeTimeMs = this.activeTimeMs + (now - this.activeStartTime);
            this.activeStartTime = now; // Reset for next calculation
        }
        // Update webcam time if currently on
        if (this.webcamStartTime !== null) {
            this.webcamOnTimeMs = this.webcamOnTimeMs + (now - this.webcamStartTime);
            this.webcamStartTime = now; // Reset for next calculation
        }
        // Calculate inactive time
        this.inactiveTimeMs = this.totalTimeMs - this.activeTimeMs;
        // Get network and battery info
        const networkInfo = this.getNetworkInfo();
        const batteryInfo = await this.getBatteryInfo();
        return {
            totalTimeMs: this.totalTimeMs,
            activeTimeMs: this.activeTimeMs,
            inactiveTimeMs: this.inactiveTimeMs,
            webcamOnTimeMs: this.webcamOnTimeMs,
            messagesCount: this.messagesCount,
            networkType: networkInfo.networkType,
            batteryLevel: batteryInfo.batteryLevel,
            isCharging: batteryInfo.isCharging,
            effectiveType: networkInfo.effectiveType,
            downlink: networkInfo.downlink,
            rtt: networkInfo.rtt,
            saveData: networkInfo.saveData,
            lastActive: new Date()
        };
    }
    /**
     * Reset all metrics
     */
    reset() {
        this.startTime = Date.now();
        this.lastUpdateTime = this.startTime;
        this.totalTimeMs = 0;
        this.activeTimeMs = 0;
        this.inactiveTimeMs = 0;
        this.webcamOnTimeMs = 0;
        this.messagesCount = 0;
        if (!document.hidden) {
            this.activeStartTime = this.startTime;
        }
        else {
            this.activeStartTime = null;
        }
        if (this.isWebcamOn) {
            this.webcamStartTime = this.startTime;
        }
    }
    /**
     * Cleanup event listeners
     */
    destroy() {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }
}
// Global singleton instance
let trackerInstance = null;
/**
 * Get or create session metrics tracker
 */
export function getSessionTracker() {
    if (!trackerInstance) {
        trackerInstance = new SessionMetricsTracker();
    }
    return trackerInstance;
}
/**
 * Destroy session tracker
 */
export function destroySessionTracker() {
    if (trackerInstance) {
        trackerInstance.destroy();
        trackerInstance = null;
    }
}
